import React, { useState, useEffect, useRef, useContext } from "react";
import {
  Box,
  Button,
  Divider,
  Grid,
  IconButton,
  Popover,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";
import SearchBar from "../headerSearchbar";
import GridIcon from "../../globalStyles/icons/grid-icon";
import ListIcon from "../../globalStyles/icons/list-icon";
import ExportIcon from "../../globalStyles/icons/export-icon";
import AppContext from "../context/context";
import { useLocation } from "react-router-dom";
import { clientService } from "../../utils/services/clientService";
import CustomTypography from "../../globalStyles/fonts";
import DateRangePickerComponent from "../date-range-picker";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import SyncIcon from "@mui/icons-material/Sync";
import { isMobile } from "../../globalStyles/mobile";
import ClientAutocomplete from "../clientAutocomplete";
import AddIcon from "@mui/icons-material/Add";
import FilterIcon from "@mui/icons-material/FilterAlt";
import FilterComponent from "../grid-filter-modal";
import { useTheme } from "@mui/styles";
import { useDarkMode } from "../context/darkModeContext";

// Enhanced View Toggle Icon - Shows grid/list with smooth transition
const DualViewToggleIcon = ({ fill = "#000", size = 24 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <defs>
      <linearGradient id="toggleGradient" x1="0%" y1="0%" x2="100%" y2="0%">
        <stop offset="0%" stopColor={fill} stopOpacity="0.9" />
        <stop offset="50%" stopColor={fill} stopOpacity="0.6" />
        <stop offset="100%" stopColor={fill} stopOpacity="0.9" />
      </linearGradient>
    </defs>

    {/* Grid view (top-left) - 4 squares */}
    <rect
      x="2"
      y="2"
      width="3.5"
      height="3.5"
      fill={fill}
      rx="0.5"
      opacity="0.85"
    />
    <rect
      x="6.5"
      y="2"
      width="3.5"
      height="3.5"
      fill={fill}
      rx="0.5"
      opacity="0.85"
    />
    <rect
      x="2"
      y="6.5"
      width="3.5"
      height="3.5"
      fill={fill}
      rx="0.5"
      opacity="0.85"
    />
    <rect
      x="6.5"
      y="6.5"
      width="3.5"
      height="3.5"
      fill={fill}
      rx="0.5"
      opacity="0.85"
    />

    {/* Center divider with arrow - indicates toggle */}
    <line
      x1="11.5"
      y1="4"
      x2="11.5"
      y2="20"
      stroke={fill}
      strokeWidth="0.8"
      opacity="0.4"
    />

    {/* Arrow pointing right */}
    <path d="M 13 11 L 15.5 8.5 L 15.5 13.5 Z" fill={fill} opacity="0.7" />

    {/* Arrow pointing left */}
    <path d="M 11 13 L 8.5 15.5 L 8.5 10.5 Z" fill={fill} opacity="0.7" />

    {/* List view (right) - 4 horizontal lines */}
    <line
      x1="14.5"
      y1="4"
      x2="21"
      y2="4"
      stroke={fill}
      strokeWidth="2"
      strokeLinecap="round"
      opacity="0.85"
    />
    <line
      x1="14.5"
      y1="8"
      x2="21"
      y2="8"
      stroke={fill}
      strokeWidth="2"
      strokeLinecap="round"
      opacity="0.85"
    />
    <line
      x1="14.5"
      y1="12"
      x2="21"
      y2="12"
      stroke={fill}
      strokeWidth="2"
      strokeLinecap="round"
      opacity="0.85"
    />
    <line
      x1="14.5"
      y1="16"
      x2="21"
      y2="16"
      stroke={fill}
      strokeWidth="2"
      strokeLinecap="round"
      opacity="0.85"
    />
    <line
      x1="14.5"
      y1="20"
      x2="21"
      y2="20"
      stroke={fill}
      strokeWidth="2"
      strokeLinecap="round"
      opacity="0.85"
    />
  </svg>
);

function Header({
  onClickUpload,
  hideUploadButton,
  hideAddButton,
  onClickAddButton,
  onHandleUploadButton,
  zipZone,
  simulation,
  onUploadSimulationData,
  rateCard,
  searchBarPlaceholder,
  onSearchData,
  title,
  client,
  workflow,
  product,
  setViewMode,
  order,
  header,
  onSelectDate,
  showDownloadIcon,
  onClickRefresh,
  customClerance,
  importProduct,
  eventMapping,
  headerButtons,
  onClickExport,
  hideFilterClient,
  onSelectClient,
  onChangeFilter,
  fields,
  filterData,
  globalDesign,
  manifest,
  filterButton = true,
  hideGridList,
  hideAllButton,
}) {
  const currentDate = new Date();
  const [selectedClientData, setSelectedClientData] = useState({ name: "" });
  const location = useLocation();
  const fileUploadRef = useRef();
  const { user } = useContext(AppContext);
  const [viewMode, setMode] = useState("list");
  const [startDate, setStartDate] = useState(dayjs());
  const [endDate, setEndDate] = useState(dayjs().add(7, "day"));
  const [selectionRange, setSelectionRange] = useState({
    startDate: new Date(currentDate.setMonth(currentDate.getMonth() - 2)),
    endDate: new Date(new Date().setDate(new Date().getDate() + 2)),
    key: "selection",
  });
  const [openFilterModal, setOpenFilterModal] = useState(false);
  const [anchorElFilter, setAnchorElFilter] = useState(null);
  const [selectedfilterData, setSelectedFilterData] = useState({
    key: "",
    name: "",
    filter: [],
    sort: 0,
    id: "_id",
    displayKey: "name",
    fetchApi: () => {},
    loadMoreButton: false,
    options: [],
    searchKey: "",
  });

  const handleFilterSave = (
    selectedValues,
    filterKey,
    sortOrder,
    searchKey,
  ) => {
    onChangeFilter({
      key: filterKey,
      filter: selectedValues,
      sort: sortOrder,
      searchKey: searchKey,
    });

    const updatedColumns = filterData.columns.map((column) =>
      column.key === filterKey
        ? {
            ...column,
            filter: selectedValues,
            sort: sortOrder,
            searchKey: searchKey,
          }
        : column,
    );

    setSelectedFilterData({
      ...selectedfilterData,
      filter: selectedValues,
      searchKey,
    });
    setOpenFilterModal(false);
  };

  const handleOpenModal = (event, filterKey) => {
    setAnchorElFilter(event.currentTarget);
    setOpenFilterModal(true);
    const fieldData = filterData?.columns?.find((x) => x.key === filterKey);
    if (fieldData) {
      setSelectedFilterData({
        ...fieldData,
        filter: fieldData.filter || [],
        searchKey: fieldData.searchKey || "",
        key: fieldData.key, // Ensure key is passed
        name: fieldData.name,
        id: fieldData.id || "_id",
        displayKey: fieldData.displayKey || "name",
        options: fieldData.options || [],
        sort: fieldData.sort || 0,
        loadMoreButton: fieldData.loadMoreButton || false,
      });
    }
  };

  const handleCloseFilter = () => {
    setAnchorElFilter(null);
    setOpenFilterModal(false);
  };
  const handleFieldSelection = (field) => {
    setSelectedFilterData((prevState) => ({
      ...prevState,
      key: field.key,
      name: field.name,
      filter: field.filter || [],
      sort: field.sort || 0,
      id: field.id || "_id",
      displayKey: field.displayKey || "name",
      fetchApi: field.fetchApi || (() => {}),
      loadMoreButton: field.loadMoreButton || false,
      options: field.options || [],
      searchKey: field.searchKey || "",
    }));
  };

  const theme = useTheme();
  const { darkMode } = useDarkMode();
  const defaultColor = "#D13438";
  const primary_color = theme.palette?.primary?.main || defaultColor;
  const [selectedClient, setSelectedClient] = useState(null);

  const handleFileUpload = (event) => {
    event.preventDefault();
    fileUploadRef.current.click();
  };

  const uploadFile = () => {
    const uploadedFile = fileUploadRef.current.files[0];
    if (simulation) {
      onUploadSimulationData && onUploadSimulationData(uploadedFile);
    }
    const formData = new FormData();
    formData.append("file", uploadedFile);
    onClickUpload && onClickUpload(formData);
    fileUploadRef.current.value = null;
  };

  const capitalize = (str) =>
    str.replace(/\b\w/g, (char) => char.toUpperCase());

  const handleGridViewClick = () => {
    setViewMode("grid");
    setMode("grid");
  };

  const handleListViewClick = () => {
    setViewMode("list");
    setMode("list");
  };

  const handleToggleViewMode = () => {
    if (viewMode === "grid") {
      handleListViewClick();
    } else {
      handleGridViewClick();
    }
  };

  const handleClearStartDate = () => {
    setStartDate(null);
    setEndDate(null);
    onSelectDate(null);
  };
  // useEffect(() => {
  //   onSelectDate && onSelectDate(selectionRange);
  // }, [selectionRange]);

  return (
    <Box sx={{ marginTop: isMobile() ? 0 : 2, width: "100%" }}>
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={isMobile() ? 6 : 12} md={4} sm={2}>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <CustomTypography
              style={{ color: (theme) => theme.palette.text.main }}
              fontSize={isMobile() ? 14 : "20px"}
              fontWeight={500}
            >
              {header}
            </CustomTypography>
          </Box>
        </Grid>
        <Grid item xs={isMobile() ? 6 : 12} md={8} sm={10}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              gap: 1,
            }}
          >
            <input
              onChange={uploadFile}
              type="file"
              id="excelFile"
              accept=".xls,.xlsx"
              ref={fileUploadRef}
              hidden
            />
            <div>{headerButtons}</div>

            {!hideUploadButton &&
              (isMobile() ? (
                <IconButton
                  style={{
                    color: primary_color,
                  }}
                  onClick={(e) =>
                    zipZone || rateCard
                      ? onHandleUploadButton(e)
                      : handleFileUpload(e)
                  }
                >
                  <UploadFileIcon />
                </IconButton>
              ) : (
                <Button
                  size="small"
                  style={{
                    textTransform: "none",
                  }}
                  color="primary"
                  variant="contained"
                  onClick={(e) =>
                    zipZone || rateCard
                      ? onHandleUploadButton(e)
                      : handleFileUpload(e)
                  }
                >
                  <UploadFileIcon style={{ marginRight: 5, height: 20 }} />
                  <CustomTypography
                    fontSize="14px"
                    fontWeight={500}
                    color="#FFFFFF"
                  >
                    Upload
                  </CustomTypography>
                </Button>
              ))}

            {!hideAddButton &&
              (isMobile() ? (
                <IconButton
                  style={{
                    color: primary_color,
                  }}
                  onClick={onClickAddButton}
                >
                  {showDownloadIcon ? <ExportIcon /> : <AddIcon />}
                </IconButton>
              ) : hideAllButton ? (
                <></>
              ) : (
                <Button
                  size="small"
                  style={{
                    textTransform: "none",
                  }}
                  color="primary"
                  variant="contained"
                  onClick={onClickAddButton}
                >
                  {showDownloadIcon && (
                    <IconButton sx={{ height: 30 }}>
                      <ExportIcon />
                    </IconButton>
                  )}
                  <CustomTypography
                    fontSize="14px"
                    fontWeight={500}
                    color="#FFFFFF"
                  >
                    {title}
                  </CustomTypography>
                </Button>
              ))}
            {onClickRefresh &&
              (isMobile() ? (
                <IconButton
                  style={{ backgroundColor: "primary.default" }}
                  onClick={onClickRefresh}
                >
                  <SyncIcon color="primary" />
                </IconButton>
              ) : (
                <Button
                  size="small"
                  style={{
                    // margin: 3,
                    textTransform: "none",
                    fontSize: 14,
                    height: 30,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                  variant="contained"
                  onClick={onClickRefresh}
                  color="primary"
                >
                  <IconButton style={{ color: "#FFFFFF" }}>
                    <SyncIcon />
                  </IconButton>
                </Button>
              ))}

            {hideAllButton ? (
              <></>
            ) : viewMode === "grid" && filterButton ? (
              <Button
                variant="outlined"
                style={{
                  border: darkMode ? "1px solid #2C3045" : "1px solid #D9D9D9",
                  backgroundColor: darkMode ? "#252A3A" : "#FFFFFF",
                  color: darkMode ? "#E2E8F0" : "#000000",
                  height: 30,
                  justifyContent: "center",
                  display: "flex",
                  textTransform: "none",
                  alignItems: "center",
                }}
                onClick={handleOpenModal}
              >
                <IconButton>
                  <FilterIcon sx={{ color: darkMode ? "#E2E8F0" : "#000" }} />
                </IconButton>
                <Typography
                  fontFamily={"poppins"}
                  fontSize={"14px"}
                  fontWeight={400}
                >
                  Filter
                </Typography>
              </Button>
            ) : (
              <></>
            )}

            {!order &&
            !zipZone &&
            !rateCard &&
            !simulation &&
            !customClerance &&
            !isMobile() &&
            !eventMapping &&
            !manifest &&
            !importProduct &&
            !hideGridList ? (
              <IconButton
                aria-label="toggle-view"
                onClick={handleToggleViewMode}
                title={`Switch to ${viewMode === "grid" ? "list" : "grid"} view`}
                style={{
                  color: primary_color || "#DC585C",
                  marginRight: 10,
                  padding: "8px",
                }}
              >
                <DualViewToggleIcon
                  fill={primary_color || "#DC585C"}
                  size={24}
                />
              </IconButton>
            ) : null}
          </Box>
        </Grid>
      </Grid>
      <Divider sx={{ my: 1, color: darkMode ? "#2C3045" : "#C0C3D7" }} />
      <Popover
        open={Boolean(anchorElFilter)}
        anchorEl={anchorElFilter}
        onClose={handleCloseFilter}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        disableScrollLock={true}
      >
        <FilterComponent
          open={openFilterModal}
          onClose={handleCloseFilter}
          fields={fields}
          options={selectedfilterData?.options}
          selectedValues={selectedfilterData?.filter}
          onSave={handleFilterSave}
          currentSortOrder={selectedfilterData?.sort}
          filterKey={selectedfilterData?.key}
          hasMore={selectedfilterData?.loadMoreButton}
          displayKey={selectedfilterData?.displayKey}
          id={selectedfilterData?.id}
          fetchApi={selectedfilterData?.fetchApi}
          searchKey={selectedfilterData?.searchKey}
          onFieldSelection={handleFieldSelection}
        />
      </Popover>
    </Box>
  );
}

export default Header;
